import React, { useState, useEffect, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { 
  XMarkIcon, 
  ArrowLeftIcon, 
  ArrowRightIcon, 
  PlayIcon,
  PauseIcon,
  CheckIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';

interface Stack {
  id: number;
  name: string;
  box_count: number;
  status: string;
}

interface BulkOperationData {
  stackId: number;
  data: any;
  completed: boolean;
  skipped: boolean;
}

interface BulkInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  operation: 'feeding';
  stacks: Stack[];
  onSubmit: (results: BulkOperationData[]) => void;
  template?: any;
}

const BulkInputModal: React.FC<BulkInputModalProps> = ({
  isOpen,
  onClose,
  operation,
  stacks,
  onSubmit,
  template
}) => {
  const [currentStackIndex, setCurrentStackIndex] = useState(0);
  const [operationData, setOperationData] = useState<Map<number, any>>(new Map());
  const [isPaused, setIsPaused] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [startTime, setStartTime] = useState<Date | null>(null);
  
  const inputRef = useRef<HTMLInputElement>(null);
  
  const activeStacks = stacks.filter(s => s.status === 'active');
  const currentStack = activeStacks[currentStackIndex];
  const progress = ((currentStackIndex) / activeStacks.length) * 100;
  const isLastStack = currentStackIndex >= activeStacks.length - 1;

  // Горячие клавиши
  useHotkeys('escape', onClose, [isOpen]);
  useHotkeys('ctrl+left,left', handlePrevious, [isOpen, currentStackIndex]);
  useHotkeys('ctrl+right,right', handleNext, [isOpen, currentStackIndex]);
  useHotkeys('ctrl+down', handleSkip, [isOpen, currentStackIndex]);
  useHotkeys('ctrl+enter', handleSaveAndNext, [isOpen, currentStackIndex]);
  useHotkeys('space', () => setIsPaused(!isPaused), [isOpen, isPaused]);

  useEffect(() => {
    if (isOpen) {
      setStartTime(new Date());
      setCurrentStackIndex(0);
      setOperationData(new Map());
      setIsPaused(false);
      
      // Загружаем шаблон если есть
      if (template) {
        const templateData = new Map();
        activeStacks.forEach(stack => {
          templateData.set(stack.id, { ...template.defaultValues });
        });
        setOperationData(templateData);
      }
      
      // Фокус на первое поле
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, template, activeStacks]);

  function handlePrevious() {
    if (currentStackIndex > 0) {
      setCurrentStackIndex(currentStackIndex - 1);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }

  function handleNext() {
    if (currentStackIndex < activeStacks.length - 1) {
      setCurrentStackIndex(currentStackIndex + 1);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }

  function handleSkip() {
    const newData = new Map(operationData);
    newData.set(currentStack.id, { ...newData.get(currentStack.id), skipped: true });
    setOperationData(newData);
    
    if (autoAdvance && !isLastStack) {
      handleNext();
    }
  }

  function handleSaveAndNext() {
    const currentData = operationData.get(currentStack.id) || {};
    
    if (validateCurrentData(currentData)) {
      const newData = new Map(operationData);
      newData.set(currentStack.id, { ...currentData, completed: true });
      setOperationData(newData);
      
      if (autoAdvance && !isLastStack) {
        handleNext();
      } else if (isLastStack) {
        handleComplete();
      }
    }
  }

  function validateCurrentData(data: any): boolean {
    switch (operation) {
      case 'feeding':
        return data.feedTypeId && data.amount > 0;
      default:
        return true;
    }
  }

  function handleComplete() {
    const results: BulkOperationData[] = activeStacks.map(stack => ({
      stackId: stack.id,
      data: operationData.get(stack.id) || {},
      completed: operationData.get(stack.id)?.completed || false,
      skipped: operationData.get(stack.id)?.skipped || false
    }));

    onSubmit(results);
    onClose();
  }

  function updateCurrentData(field: string, value: any) {
    const newData = new Map(operationData);
    const currentData = newData.get(currentStack.id) || {};
    newData.set(currentStack.id, { ...currentData, [field]: value });
    setOperationData(newData);
  }

  function renderOperationForm() {
    const currentData = operationData.get(currentStack?.id) || {};

    switch (operation) {
      case 'feeding':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Тип корма
              </label>
              <select
                value={currentData.feedTypeId || ''}
                onChange={(e) => updateCurrentData('feedTypeId', Number(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Выберите корм</option>
                <option value={1}>Отруби пшеничные</option>
                <option value={2}>Пшеница дробленая</option>
                <option value={3}>Жмых подсолнечника</option>
                <option value={4}>Смешанный корм</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Количество (кг)
              </label>
              <input
                ref={inputRef}
                type="number"
                value={currentData.amount || ''}
                onChange={(e) => updateCurrentData('amount', Number(e.target.value))}
                step="0.1"
                min="0.1"
                max="10"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="1.5"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Активность поедания
              </label>
              <select
                value={currentData.activity || 'medium'}
                onChange={(e) => updateCurrentData('activity', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="low">Низкая</option>
                <option value="medium">Средняя</option>
                <option value="high">Высокая</option>
              </select>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  }

  if (!isOpen) return null;

  const operationTitles = {
    feeding: '🥗 Массовое кормление'
  };

  const completedCount = Array.from(operationData.values()).filter(d => d.completed).length;
  const skippedCount = Array.from(operationData.values()).filter(d => d.skipped).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        
        {/* Заголовок */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {operationTitles[operation]}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Обработка {activeStacks.length} стеллажей
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex">
          {/* Список стеллажей (левая панель) */}
          <div className="w-1/3 border-r border-gray-200 p-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600 mb-4">
                <span>Прогресс: {completedCount}/{activeStacks.length}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              
              {/* Прогресс бар */}
              <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>

              {/* Список стеллажей */}
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {activeStacks.map((stack, index) => {
                  const stackData = operationData.get(stack.id);
                  const isCompleted = stackData?.completed;
                  const isSkipped = stackData?.skipped;
                  const isCurrent = index === currentStackIndex;

                  return (
                    <button
                      key={stack.id}
                      onClick={() => setCurrentStackIndex(index)}
                      className={`w-full text-left p-2 rounded-lg transition-all ${
                        isCurrent
                          ? 'bg-blue-500 text-white'
                          : isCompleted
                          ? 'bg-green-100 text-green-800'
                          : isSkipped
                          ? 'bg-gray-100 text-gray-500'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{stack.name}</span>
                        {isCompleted && <CheckIcon className="w-4 h-4" />}
                        {isSkipped && <ArrowDownIcon className="w-4 h-4" />}
                        {isCurrent && <span className="text-xs">●</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Форма ввода (правая панель) */}
          <div className="flex-1 p-6">
            {currentStack && (
              <>
                {/* Информация о текущем стеллаже */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    📦 {currentStack.name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    Стеллаж {currentStackIndex + 1} из {activeStacks.length} • {currentStack.box_count} ящиков
                  </p>
                </div>

                {/* Состояние паузы */}
                {isPaused && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center text-yellow-800">
                      <PauseIcon className="w-5 h-5 mr-2" />
                      <span>Режим на паузе. Нажмите Space для продолжения.</span>
                    </div>
                  </div>
                )}

                {/* Форма операции */}
                <div className="mb-6">
                  {renderOperationForm()}
                </div>

                {/* Настройки */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={autoAdvance}
                      onChange={(e) => setAutoAdvance(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">
                      Автоматически переходить к следующему стеллажу
                    </span>
                  </label>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Нижняя панель с кнопками */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-4">
            <button
              onClick={handlePrevious}
              disabled={currentStackIndex === 0}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 disabled:text-gray-400 transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              <span>Назад</span>
              <kbd className="px-2 py-1 text-xs bg-gray-200 rounded">←</kbd>
            </button>
            
            <button
              onClick={() => setIsPaused(!isPaused)}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              {isPaused ? <PlayIcon className="w-4 h-4" /> : <PauseIcon className="w-4 h-4" />}
              <span>{isPaused ? 'Продолжить' : 'Пауза'}</span>
              <kbd className="px-2 py-1 text-xs bg-gray-200 rounded">Space</kbd>
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleSkip}
              className="flex items-center space-x-2 px-4 py-2 text-yellow-600 hover:text-yellow-800 transition-colors"
            >
              <ArrowDownIcon className="w-4 h-4" />
              <span>Пропустить</span>
              <kbd className="px-2 py-1 text-xs bg-gray-200 rounded">Ctrl+↓</kbd>
            </button>
            
            <button
              onClick={handleNext}
              disabled={isLastStack}
              className="flex items-center space-x-2 px-4 py-2 text-blue-600 hover:text-blue-800 disabled:text-gray-400 transition-colors"
            >
              <span>Далее</span>
              <ArrowRightIcon className="w-4 h-4" />
              <kbd className="px-2 py-1 text-xs bg-gray-200 rounded">→</kbd>
            </button>
            
            <button
              onClick={isLastStack ? handleComplete : handleSaveAndNext}
              className="flex items-center space-x-2 px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              <CheckIcon className="w-4 h-4" />
              <span>{isLastStack ? 'Завершить' : 'Сохранить'}</span>
              <kbd className="px-2 py-1 text-xs bg-black bg-opacity-20 rounded">Ctrl+Enter</kbd>
            </button>
          </div>
        </div>

        {/* Статистика времени */}
        {startTime && (
          <div className="px-6 py-2 text-xs text-gray-500 border-t border-gray-200">
            Время работы: {Math.round((Date.now() - startTime.getTime()) / 1000)} сек
            {completedCount > 0 && (
              <> • Среднее время на стеллаж: {Math.round((Date.now() - startTime.getTime()) / 1000 / completedCount)} сек</>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkInputModal;
